import { PaginatedResponse } from "@/types";
import { AttendeeSaleView, Sale, SaleDTO } from "@/types/sale";
import { apiUrl } from "@/utils/url";
import { HttpClient } from "../HttpClient";
import { Employee } from "../agenda/types";
import { getErrorMessage, isAbortError, toServiceError } from "../errors";
import {
  ISaleService,
  LinkCandidatesParams,
  SalesByAttendeeAndTypeParams,
} from "./ISaleService";

export class SaleService implements ISaleService {
  private httpClient: HttpClient;
  constructor() {
    this.httpClient = new HttpClient({
      baseURL: apiUrl,
    });
  }

  async getSaleById(saleId: number): Promise<Sale> {
    try {
      const response = await this.httpClient.get<Sale>(`/sales/${saleId}`);
      return response;
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Não foi possível carregar a venda. Atualize a página e confirme se a venda ainda existe.",
      );
      throw new Error(errorMessage);
    }
  }

  async createSale(sale: SaleDTO): Promise<number> {
    try {
      const response = await this.httpClient.post<number>(
        "/sales",
        sale,
        {},
        true,
      );
      return response;
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Não foi possível criar a venda. Confirme os produtos, o funcionário e a loja selecionada e tente novamente.",
      );
      throw new Error(errorMessage);
    }
  }

  async updateSale(id: number, sale: SaleDTO): Promise<number> {
    try {
      const response = await this.httpClient.put<number>(
        `/sales/${id}`,
        sale,
        {},
        true,
      );
      return response;
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Não foi possível guardar a venda. Confirme se os produtos ainda estão disponíveis e tente novamente.",
      );
      throw new Error(errorMessage);
    }
  }

  async updateSaleStatus(id: number, statusCode: string): Promise<number> {
    try {
      const response = await this.httpClient.patch<number>(
        `/sales/${id}/update-status?newStatusCode=${statusCode}`,
        {},
        {},
        true,
      );
      return response;
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Não foi possível alterar o estado da venda. Atualize a página para confirmar o estado atual e tente novamente.",
      );
      throw new Error(errorMessage);
    }
  }

  async replaceSalePaymentBySaleId(saleId: number): Promise<number> {
    try {
      const response = await this.httpClient.post<number>(
        `/sales/${saleId}/payment/replace`,
        {},
        {},
        true,
      );
      return response;
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Não foi possível criar um novo pagamento para esta venda. Atualize a página e tente criar um novo pagamento.",
      );
      throw new Error(errorMessage);
    }
  }

  async getSalesByAttendeeIdAndTypeCode({
    attendeeId,
    typeCode,
    storeId,
    page,
    size,
    sort,
    startDate,
    endDate,
    onlyShowReOpened,
    saleStatusCode,
    serviceTypeId,
  }: SalesByAttendeeAndTypeParams): Promise<
    PaginatedResponse<AttendeeSaleView>
  > {
    try {
      const params = new URLSearchParams();
      if (page !== undefined) params.append("page", String(page));
      if (size !== undefined) params.append("size", String(size));
      if (sort) params.append("sort", sort);
      const query = params.toString();

      const searchParameters = {
        ...(typeCode ? { saleTypeCode: typeCode } : {}),
        ...(saleStatusCode ? { saleStatusCode } : {}),
        ...(storeId !== undefined && storeId !== null ? { storeId } : {}),
        ...(serviceTypeId !== undefined ? { serviceTypeId } : {}),
        ...(startDate ? { startDate } : {}),
        ...(endDate ? { endDate } : {}),
        ...(onlyShowReOpened === true ? { onlyShowReOpened: true } : {}),
      };

      const response = await this.httpClient.post<
        PaginatedResponse<AttendeeSaleView>
      >(
        `/sales/attendee/${attendeeId}/search${query ? `?${query}` : ""}`,
        searchParameters,
      );
      return response;
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Não foi possível carregar as vendas do animal. Atualize a página e tente novamente.",
      );
      throw new Error(errorMessage);
    }
  }

  async reopenSale(id: number): Promise<number> {
    try {
      const response = await this.httpClient.put<number>(
        `/sales/${id}/re-open`,
        {},
        {},
        true,
      );
      return response;
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Não foi possível reabrir a venda. Confirme se a venda está concluída e tente novamente. Esta ação pode reabrir a venda e iniciar o reembolso associado.",
      );
      throw new Error(errorMessage);
    }
  }

  async cancelPayment(id: number): Promise<number> {
    try {
      const response = await this.httpClient.put<number>(
        `/sales/${id}/payment/cancel`,
        {},
        {},
        true,
      );
      return response;
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Não foi possível cancelar o pagamento. Confirme se a venda está no estado permitido para cancelar pagamento. Se o pagamento já tiver sido processado, contacte a equipa de Suporte.",
      );
      throw new Error(errorMessage);
    }
  }

  async changeBudgetToSale(
    budgetId: number,
    targetSaleId?: number,
  ): Promise<number> {
    try {
      const endpoint = targetSaleId
        ? `/sales/budget/${budgetId}/to-sale?targetSaleId=${targetSaleId}`
        : `/sales/budget/${budgetId}/to-sale`;
      const response = await this.httpClient.post<number>(
        endpoint,
        null,
        {},
        true,
      );

      return response;
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Não foi possível converter o orçamento em venda. Confirme se o orçamento pertence ao mesmo animal e loja da venda.",
      );
      throw new Error(errorMessage);
    }
  }

  async getEmployeesToInvoiceSale(saleId: number): Promise<Employee[]> {
    try {
      const response = await this.httpClient.get<Employee[]>(
        `/sales/${saleId}/invoice/eligible-employees`,
      );
      return response;
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Não foi possível carregar os funcionários para faturar a venda. Atualize a página e tente novamente.",
      );
      throw new Error(errorMessage);
    }
  }

  async deleteBudget(budgetId: number): Promise<void> {
    try {
      await this.httpClient.delete(`/sales/budget/${budgetId}`);
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Não foi possível eliminar o orçamento. Confirme se o orçamento ainda existe e pode ser alterado.",
      );
      throw new Error(errorMessage);
    }
  }

  async getLinkCandidates(
    saleId: number,
    {
      linkToParent,
      searchTerm,
      page,
      size,
      sort,
      signal,
    }: LinkCandidatesParams = {},
  ): Promise<PaginatedResponse<Sale>> {
    try {
      const params = new URLSearchParams();
      if (linkToParent) params.append("linkToParent", "true");
      const trimmedSearch = searchTerm?.trim();
      if (trimmedSearch) params.append("searchTerm", trimmedSearch);
      if (page !== undefined) params.append("page", String(page));
      if (size !== undefined) params.append("size", String(size));
      if (sort) params.append("sort", sort);
      const query = params.toString();
      const response = await this.httpClient.get<PaginatedResponse<Sale>>(
        `/sales/${saleId}/link/candidates${query ? `?${query}` : ""}`,
        { signal },
      );
      return response;
    } catch (error) {
      if (isAbortError(error)) throw error;
      throw toServiceError(
        error,
        "Não foi possível carregar as vendas por pagar deste cliente. Atualize a página e tente novamente.",
      );
    }
  }

  async linkChildSale(
    parentSaleId: number,
    childSaleId: number,
  ): Promise<void> {
    try {
      await this.httpClient.put(`/sales/${parentSaleId}/link/${childSaleId}`);
    } catch (error) {
      throw toServiceError(
        error,
        "Não foi possível associar a venda à venda agregada. Atualize a página e confirme se as vendas ainda estão por pagar.",
      );
    }
  }

  async unlinkChildSale(
    parentSaleId: number,
    childSaleId: number,
  ): Promise<void> {
    try {
      await this.httpClient.delete(
        `/sales/${parentSaleId}/link/${childSaleId}`,
      );
    } catch (error) {
      throw toServiceError(
        error,
        "Não foi possível desassociar a venda da venda agregada. Atualize a página e confirme o estado do pagamento.",
      );
    }
  }
}
